import { useEffect, useRef, useState, useMemo } from "react";
import {
  getLeads,
  createLead,
  updateLead,
  deleteLead,
  importLeads,
} from "../../services/campaignLeadsApi";
import { getLeadGroups, createLeadGroup } from "../../services/leadGroupsApi";
import { normalizeApiError } from "../../utils/errors";
import Badge from "../common/Badge";
import Button from "../common/Button";
import EmptyState from "../common/EmptyState";

// ─── Status helpers ───────────────────────────────────────────────────────────
const STATUS_META = {
  active:       { label: "Active",       variant: "green" },
  inactive:     { label: "Inactive",     variant: "default" },
  unsubscribed: { label: "Unsubscribed", variant: "danger" },
};
function getStatusMeta(s) { return STATUS_META[s] || { label: s || "Active", variant: "green" }; }

// ─── Shared: Contact Groups picker ───────────────────────────────────────────
function GroupsPicker({ groups, selected, onToggle, newName, onNewNameChange, onCreateGroup, creating }) {
  return (
    <div className="lm-groups-picker">
      <div className="lm-groups-picker-label">Contact Groups</div>
      <div className="lm-groups-chips-row">
        {groups.map((g) => (
          <button
            key={g.group_id}
            type="button"
            className={`lm-group-chip${selected.includes(g.group_id) ? " selected" : ""}`}
            onClick={() => onToggle(g.group_id)}
          >
            {g.name}
            <span className="lm-group-chip-count">{g.member_count ?? 0}</span>
            {selected.includes(g.group_id) && <span className="lm-group-chip-x">×</span>}
          </button>
        ))}
        <div className="lm-group-inline-create">
          <input
            type="text"
            className="lm-group-inline-input"
            placeholder="New group name…"
            value={newName}
            onChange={(e) => onNewNameChange(e.target.value)}
          />
          <button
            type="button"
            className="lm-group-inline-btn"
            disabled={!newName.trim() || creating}
            onClick={onCreateGroup}
          >
            {creating ? "…" : "+ Create"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Add / Edit Lead modal ────────────────────────────────────────────────────
function LeadFormModal({ groups, initial, onClose, onSave, showToast }) {
  const isEdit = Boolean(initial?.lead_id);
  const [name, setName] = useState(initial?.name || "");
  const [phone, setPhone] = useState(initial?.phone || "");
  const [email, setEmail] = useState(initial?.email || "");
  const [status, setStatus] = useState(initial?.status || "active");
  const [notes, setNotes] = useState(initial?.notes || "");
  const [groupIds, setGroupIds] = useState(initial?.group_ids || []);
  const [newGroupName, setNewGroupName] = useState("");
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [busy, setBusy] = useState(false);

  function toggleGroup(gid) {
    setGroupIds((prev) => prev.includes(gid) ? prev.filter((id) => id !== gid) : [...prev, gid]);
  }

  async function handleCreateGroup() {
    if (!newGroupName.trim()) return;
    setCreatingGroup(true);
    try {
      const result = await createLeadGroup({ name: newGroupName.trim() });
      const newId = result?.group?.group_id || result?.group_id;
      if (newId) setGroupIds((prev) => [...prev, newId]);
      setNewGroupName("");
      showToast(`Group "${newGroupName.trim()}" created.`, false, 3000);
      await onSave.__refreshGroups?.();
    } catch (err) {
      showToast(normalizeApiError(err).message, true);
    } finally {
      setCreatingGroup(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!phone.trim()) { showToast("Phone number is required.", true); return; }
    setBusy(true);
    try {
      if (isEdit) {
        await updateLead(initial.lead_id, { name: name.trim() || null, email: email.trim() || null, status, notes: notes.trim() || null, group_ids: groupIds });
        showToast("Lead updated.", false, 3000);
      } else {
        await createLead({ name: name.trim() || null, phone: phone.trim(), email: email.trim() || null, status, notes: notes.trim() || null, group_ids: groupIds });
        showToast("Lead added.", false, 3000);
      }
      onSave();
    } catch (err) {
      showToast(normalizeApiError(err).message, true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="lm-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="lm-modal">
        <div className="lm-modal-header">
          <span className="lm-modal-title">{isEdit ? "Edit Lead" : "Add New Lead"}</span>
          <button className="lm-modal-close" onClick={onClose}>×</button>
        </div>
        <form className="lm-modal-body" onSubmit={handleSubmit}>
          <GroupsPicker
            groups={groups}
            selected={groupIds}
            onToggle={toggleGroup}
            newName={newGroupName}
            onNewNameChange={setNewGroupName}
            onCreateGroup={handleCreateGroup}
            creating={creatingGroup}
          />

          <div className="lm-form-row">
            <label className="lm-form-field">
              <span>Full Name</span>
              <input className="lm-input" type="text" placeholder="e.g. John Doe" value={name} onChange={(e) => setName(e.target.value)} disabled={busy} />
            </label>
            <label className="lm-form-field required">
              <span>Phone Number *</span>
              <input className="lm-input" type="text" placeholder="+971501234567" value={phone} onChange={(e) => setPhone(e.target.value)} disabled={busy || isEdit} required />
            </label>
          </div>

          <div className="lm-form-row">
            <label className="lm-form-field">
              <span>Email</span>
              <input className="lm-input" type="email" placeholder="john@example.com" value={email} onChange={(e) => setEmail(e.target.value)} disabled={busy} />
            </label>
            <label className="lm-form-field">
              <span>Status</span>
              <select className="lm-input lm-select" value={status} onChange={(e) => setStatus(e.target.value)} disabled={busy}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="unsubscribed">Unsubscribed</option>
              </select>
            </label>
          </div>

          <label className="lm-form-field">
            <span>Notes</span>
            <textarea className="lm-input lm-textarea" placeholder="Any notes about this lead…" value={notes} onChange={(e) => setNotes(e.target.value)} disabled={busy} rows={2} />
          </label>

          <div className="lm-modal-footer">
            <Button onClick={onClose} disabled={busy}>Cancel</Button>
            <Button type="submit" variant="gold" disabled={busy}>{busy ? "Saving…" : isEdit ? "Save Changes" : "Add Contact"}</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Import modal ─────────────────────────────────────────────────────────────
function ImportModal({ groups, onClose, onDone, showToast }) {
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [groupIds, setGroupIds] = useState([]);
  const [newGroupName, setNewGroupName] = useState("");
  const [creatingGroup, setCreatingGroup] = useState(false);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  function toggleGroup(gid) {
    setGroupIds((prev) => prev.includes(gid) ? prev.filter((id) => id !== gid) : [...prev, gid]);
  }

  async function handleCreateGroup() {
    if (!newGroupName.trim()) return;
    setCreatingGroup(true);
    try {
      const result = await createLeadGroup({ name: newGroupName.trim() });
      const newId = result?.group?.group_id || result?.group_id;
      if (newId) setGroupIds((prev) => [...prev, newId]);
      setNewGroupName("");
      showToast(`Group "${newGroupName.trim()}" created.`, false, 3000);
      await onDone.__refreshGroups?.();
    } catch (err) {
      showToast(normalizeApiError(err).message, true);
    } finally {
      setCreatingGroup(false);
    }
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragging(false);
    const dropped = e.dataTransfer.files[0];
    if (dropped) setFile(dropped);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!file) { showToast("Please select a CSV or Excel file.", true); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (groupIds.length) fd.append("group_ids", JSON.stringify(groupIds));
      const result = await importLeads(fd);
      showToast(`Imported ${result?.added ?? 0} contacts (${result?.failed ?? 0} failed).`, false, 4000);
      onDone();
    } catch (err) {
      showToast(normalizeApiError(err).message, true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="lm-modal-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="lm-modal">
        <div className="lm-modal-header">
          <span className="lm-modal-title">Import Contacts</span>
          <button className="lm-modal-close" onClick={onClose}>×</button>
        </div>
        <form className="lm-modal-body" onSubmit={handleSubmit}>
          <GroupsPicker
            groups={groups}
            selected={groupIds}
            onToggle={toggleGroup}
            newName={newGroupName}
            onNewNameChange={setNewGroupName}
            onCreateGroup={handleCreateGroup}
            creating={creatingGroup}
          />

          <div
            className={`lm-drop-zone${dragging ? " dragging" : ""}${file ? " has-file" : ""}`}
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={handleDrop}
          >
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: "none" }} onChange={(e) => setFile(e.target.files[0] || null)} />
            {file ? (
              <>
                <div className="lm-drop-icon">📄</div>
                <div className="lm-drop-filename">{file.name}</div>
                <div className="lm-drop-hint">Click to change file</div>
              </>
            ) : (
              <>
                <div className="lm-drop-icon">📂</div>
                <div className="lm-drop-text">Drop CSV / Excel file here</div>
                <div className="lm-drop-hint">or click to browse — columns: name, phone, email</div>
              </>
            )}
          </div>

          <div className="lm-modal-footer">
            <Button onClick={onClose} disabled={busy}>Cancel</Button>
            <Button type="submit" variant="gold" disabled={busy || !file}>{busy ? "Importing..." : "Import Contacts"}</Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Actions menu (per row) ───────────────────────────────────────────────────
function ActionsMenu({ lead, onEdit, onDelete }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    function handler(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  return (
    <div className="lm-actions-menu" ref={ref}>
      <button className="lm-actions-trigger" onClick={() => setOpen((v) => !v)}>⋯</button>
      {open && (
        <div className="lm-actions-dropdown">
          <button onClick={() => { setOpen(false); onEdit(lead); }}>Edit</button>
          <button className="danger" onClick={() => { setOpen(false); onDelete(lead); }}>Delete</button>
        </div>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function LeadManagementTab({ showToast, onRefreshReady, onDataLoaded }) {
  const [leads, setLeads] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterGroup, setFilterGroup] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [editing, setEditing] = useState(null);

  async function loadAll() {
    setLoading(true);
    try {
      const [leadsData, groupsData] = await Promise.all([getLeads(), getLeadGroups()]);
      setLeads(leadsData.items || []);
      setGroups(groupsData.items || []);
      onDataLoaded?.();
    } catch (err) {
      showToast(normalizeApiError(err).message, true);
    } finally {
      setLoading(false);
    }
  }

  async function loadGroups() {
    try {
      const data = await getLeadGroups();
      setGroups(data.items || []);
    } catch { /* silent */ }
  }

  useEffect(() => {
    loadAll();
    onRefreshReady?.(loadAll);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Attach refreshGroups helper so modals can refresh group list after inline creation
  const saveHelper = { __refreshGroups: loadGroups };
  const doneHelper = { __refreshGroups: loadGroups };

  const filtered = useMemo(() => {
    let list = leads;
    if (search) {
      const kw = search.toLowerCase();
      list = list.filter((l) =>
        (l.name || "").toLowerCase().includes(kw) ||
        (l.phone || "").includes(kw) ||
        (l.email || "").toLowerCase().includes(kw)
      );
    }
    if (filterGroup) {
      const gid = Number(filterGroup);
      list = list.filter((l) => l.group_ids?.includes(gid));
    }
    if (filterStatus) {
      list = list.filter((l) => (l.status || "active") === filterStatus);
    }
    return list;
  }, [leads, search, filterGroup, filterStatus]);

  const stats = useMemo(() => ({
    total: leads.length,
    active: leads.filter((l) => !l.status || l.status === "active").length,
    groups: groups.length,
    unsubscribed: leads.filter((l) => l.status === "unsubscribed").length,
  }), [leads, groups]);

  async function handleDelete(lead) {
    if (!window.confirm(`Delete lead "${lead.name || lead.phone}"? This cannot be undone.`)) return;
    try {
      await deleteLead(lead.lead_id);
      showToast("Lead deleted.", false, 3000);
      loadAll();
    } catch (err) {
      showToast(normalizeApiError(err).message, true);
    }
  }

  function handleSaved() {
    setShowAdd(false);
    setEditing(null);
    loadAll();
  }

  function handleImportDone() {
    setShowImport(false);
    loadAll();
  }

  return (
    <div className="tab-panel active lm-root" id="panel-leads" role="tabpanel">

      {/* ── Stats row ── */}
      <div className="lm-stats-row">
        <div className="lm-stat-card">
          <div className="lm-stat-value">{stats.total}</div>
          <div className="lm-stat-label">Total Contacts</div>
        </div>
        <div className="lm-stat-card green">
          <div className="lm-stat-value">{stats.active}</div>
          <div className="lm-stat-label">Active</div>
        </div>
        <div className="lm-stat-card blue">
          <div className="lm-stat-value">{stats.groups}</div>
          <div className="lm-stat-label">Groups</div>
        </div>
        <div className="lm-stat-card red">
          <div className="lm-stat-value">{stats.unsubscribed}</div>
          <div className="lm-stat-label">Unsubscribed</div>
        </div>
      </div>

      {/* ── Toolbar ── */}
      <div className="lm-toolbar">
        <div className="lm-search-wrap">
          <span className="lm-search-icon">🔍</span>
          <input
            className="lm-search-input"
            type="text"
            placeholder="Search by name, phone or email…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button className="lm-search-clear" onClick={() => setSearch("")}>×</button>
          )}
        </div>

        <select className="lm-filter-select" value={filterGroup} onChange={(e) => setFilterGroup(e.target.value)}>
          <option value="">All Groups</option>
          {groups.map((g) => (
            <option key={g.group_id} value={g.group_id}>{g.name} ({g.member_count ?? 0})</option>
          ))}
        </select>

        <select className="lm-filter-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
          <option value="">All Status</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
          <option value="unsubscribed">Unsubscribed</option>
        </select>

        <div className="lm-toolbar-actions">
          <Button onClick={() => setShowImport(true)}>Import</Button>
          <Button variant="gold" onClick={() => { setEditing(null); setShowAdd(true); }}>+ Add Contact</Button>
        </div>
      </div>

      {/* ── Count ── */}
      <div className="lm-count-line">
        {loading ? "Loading…" : `${filtered.length} of ${leads.length} contact${leads.length !== 1 ? "s" : ""}`}
      </div>

      {/* ── Table ── */}
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Phone</th>
              <th>Email</th>
              <th>Groups</th>
              <th>Status</th>
              <th>Date Added</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="7"><div className="empty-state">Loading contacts…</div></td></tr>
            ) : !filtered.length ? (
              <tr>
                <td colSpan="7">
                  <EmptyState
                    icon="👥"
                    title={leads.length === 0 ? "No contacts yet" : "No matches"}
                    subtitle={leads.length === 0 ? "Click \"+ Add Contact\" or import a CSV to get started." : "Try adjusting your search or filters."}
                  />
                </td>
              </tr>
            ) : (
              filtered.map((lead) => {
                const sm = getStatusMeta(lead.status);
                return (
                  <tr key={lead.lead_id}>
                    <td><strong>{lead.name || <span className="muted-text">—</span>}</strong></td>
                    <td className="td-phone">{lead.phone}</td>
                    <td>{lead.email || <span className="muted-text">—</span>}</td>
                    <td>
                      <div className="lm-row-groups">
                        {lead.group_names?.length ? lead.group_names.map((n, i) => (
                          <span
                            key={i}
                            className="lm-row-group-tag"
                            onClick={() => {
                              const gid = lead.group_ids?.[i];
                              if (gid) setFilterGroup(String(gid));
                            }}
                          >{n}</span>
                        )) : <span className="muted-text">—</span>}
                      </div>
                    </td>
                    <td><Badge variant={sm.variant}>{sm.label}</Badge></td>
                    <td className="muted-text" style={{ fontSize: "0.8rem" }}>
                      {lead.created_at ? new Date(lead.created_at).toLocaleDateString() : "—"}
                    </td>
                    <td>
                      <ActionsMenu
                        lead={lead}
                        onEdit={(l) => { setEditing(l); setShowAdd(true); }}
                        onDelete={handleDelete}
                      />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* ── Add / Edit modal ── */}
      {showAdd && (
        <LeadFormModal
          groups={groups}
          initial={editing}
          onClose={() => { setShowAdd(false); setEditing(null); }}
          onSave={Object.assign(handleSaved, saveHelper)}
          showToast={showToast}
        />
      )}

      {/* ── Import modal ── */}
      {showImport && (
        <ImportModal
          groups={groups}
          onClose={() => setShowImport(false)}
          onDone={Object.assign(handleImportDone, doneHelper)}
          showToast={showToast}
        />
      )}
    </div>
  );
}
